import { Check, Copy, Info } from "lucide-react";
import React, { Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import GithubIcon from "src/components/ui/icons/github";
import RootlyIcon from "src/components/ui/icons/rootly";
import TierZeroIcon from "src/components/ui/icons/tierzero_mark";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { getBotName, getProductName } from "src/utils/brandTheme";

import type { GithubIntegrationCreateGitHubInstallationLinkMutation } from "./__generated__/GithubIntegrationCreateGitHubInstallationLinkMutation.graphql";
import type { GithubIntegrationOutboundIpsQuery } from "./__generated__/GithubIntegrationOutboundIpsQuery.graphql";

interface GithubIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  source?: string;
  brandTheme?: string | null;
}

const createGitHubInstallationLinkMutation = graphql`
  mutation GithubIntegrationCreateGitHubInstallationLinkMutation($input: CreateGitHubInstallationLinkInput) {
    createGithubInstallationLink(input: $input) {
      installationLink
    }
  }
`;

const githubIntegrationOutboundIpsQuery = graphql`
  query GithubIntegrationOutboundIpsQuery {
    outboundIpAddresses
  }
`;

const IpAllowlistNote = React.memo<{ brandTheme?: string | null }>(function IpAllowlistNote({ brandTheme }) {
  const productName = getProductName(brandTheme);
  const { toast } = useToast();
  const data = useLazyLoadQuery<GithubIntegrationOutboundIpsQuery>(
    githubIntegrationOutboundIpsQuery,
    {},
  );
  const outboundIpAddresses = data?.outboundIpAddresses ?? [];

  if (outboundIpAddresses.length === 0) return null;

  const handleCopy = (ip: string) => {
    navigator.clipboard.writeText(ip).then(
      () => toast({ title: "IP copied to clipboard", variant: "success" }),
      () => toast({ title: "Failed to copy IP", variant: "error" }),
    );
  };

  return (
    <div className="flex gap-2 rounded-md border border-border bg-accent/50 p-3 text-sm text-muted-foreground">
      <Info className="h-4 w-4 mt-0.5 shrink-0" />
      <div>
        <p>
          If your GitHub organization has{" "}
          <a
            href="https://docs.github.com/en/organizations/keeping-your-organization-secure/managing-security-settings-for-your-organization/managing-allowed-ip-addresses-for-your-organization"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            IP allowlist
          </a>{" "}
          enabled, add {productName}&apos;s outbound IPs:
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {outboundIpAddresses.map((ip) => (
            <div
              key={ip}
              className="inline-flex items-center bg-accent border border-border rounded-md px-3 py-1 font-mono text-xs text-foreground"
            >
              <span className="select-all">{ip}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-2 h-4 w-4 p-0 hover:bg-muted"
                onClick={() => handleCopy(ip)}
                title="Copy to clipboard"
              >
                <Copy className="h-3 w-3 shrink-0" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export default function GithubIntegration({ isOpen, onClose, source, brandTheme }: GithubIntegrationProps) {
  const [isCopied, setIsCopied] = useState(false);
  const productName = getProductName(brandTheme);
  const partnerName = getBotName(brandTheme);
  const PartnerIcon = partnerName === "Rootly" ? RootlyIcon : TierZeroIcon;
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");
  const [installationLink, setInstallationLink] = useState<string | null>(null);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isCopyingLink, setIsCopyingLink] = useState(false);
  const [commitCreateGitHubInstallationLink] = useMutation<GithubIntegrationCreateGitHubInstallationLinkMutation>(
    createGitHubInstallationLinkMutation
  );
  const { toast } = useToast();

  const handleCopyLink = () => {
    // Generate the link if we don't have it yet
    if (!installationLink) {
      setIsCopyingLink(true);
      commitCreateGitHubInstallationLink({
        variables: { input: source ? { source } : {} },
        onCompleted: (response) => {
          setIsCopyingLink(false);
          const link = response.createGithubInstallationLink?.installationLink;
          if (link) {
            setInstallationLink(link);
            navigator.clipboard.writeText(link).then(
              () => {
                setIsCopied(true);
                toast({
                  title: "Installation link copied to clipboard",
                  variant: "success",
                });

                setTimeout(() => {
                  setIsCopied(false);
                }, 2000);
              },
              () => {
                toast({
                  title: "Failed to copy link",
                  description: "Please try again",
                  variant: "error",
                });
              }
            );
          } else {
            toast({
              title: "Failed to generate installation link",
              description: "No link was returned from the server",
              variant: "error",
            });
          }
        },
        onError: (error) => {
          setIsCopyingLink(false);
          toast({
            title: "Failed to generate installation link",
            description: error.message,
            variant: "error",
          });
        },
      });
    } else {
      // Link already exists, just copy it
      navigator.clipboard.writeText(installationLink).then(
        () => {
          setIsCopied(true);
          toast({
            title: "Installation link copied to clipboard",
            variant: "success",
          });

          setTimeout(() => {
            setIsCopied(false);
          }, 2000);
        },
        () => {
          toast({
            title: "Failed to copy link",
            description: "Please try again",
            variant: "error",
          });
        }
      );
    }
  };

  const handleInstallClick = () => {
    setIsInstalling(true);
    commitCreateGitHubInstallationLink({
      variables: { input: source ? { source } : {} },
      onCompleted: (response) => {
        setIsInstalling(false);
        const link = response.createGithubInstallationLink?.installationLink;
        if (link) {
          // Navigate to GitHub installation in the same window
          window.location.href = link;
        } else {
          toast({
            title: "Failed to generate installation link",
            description: "No link was returned from the server",
            variant: "error",
          });
        }
      },
      onError: (error) => {
        setIsInstalling(false);
        toast({
          title: "Failed to create installation link",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle>Connect GitHub</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Install {productName} to your GitHub to enable repository access and commit analysis.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          <div className="flex flex-col items-center justify-center py-4">
            <div className="flex items-center justify-center mb-8">
              <GithubIcon className="h-10 w-10" />
              <span className="mx-2 text-xl">+</span>
              <PartnerIcon className="h-10 w-10 p-1 rounded-sm bg-white text-blue-600" />
            </div>

            <div className="w-full max-w-xl space-y-6">
              <Tooltip
                content={!canModify ? "You don't have permission to connect integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <button
                    onClick={handleInstallClick}
                    disabled={isInstalling || !canModify}
                    className="w-full bg-[#24292f] text-white px-4 py-2 rounded-md flex items-center justify-center space-x-2 hover:bg-[#2d3339] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#24292f]"
                  >
                    <GithubIcon className="h-5 w-5" />
                    <span>{isInstalling ? "Loading..." : "Install on GitHub"}</span>
                  </button>
                </span>
              </Tooltip>

              <div className="pt-6 border-t border-accent">
                <p className="text-sm text-muted-foreground text-center mb-3">
                  Not a GitHub admin? Share the install link with your admin.
                </p>
                <Button
                  onClick={handleCopyLink}
                  variant="outline"
                  disabled={isCopyingLink}
                  className="w-full border-border hover:bg-card"
                >
                  {isCopied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Install Link
                    </>
                  )}
                </Button>
              </div>

              <ErrorBoundary fallback={null}>
                <Suspense fallback={null}>
                  <IpAllowlistNote brandTheme={brandTheme} />
                </Suspense>
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
